import { Divider, Flex, Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { Fragment, memo } from 'react';
import {
    DifferenceWarning,
    SummaryLineTotal,
    SummaryLineValues,
    SummarySection,
    WarningTooltip,
} from 'shared/components';

import { getXeroContext } from '../../../selectors/xero';
import { XeroCisDeduction } from '../XeroCisDeduction/XeroCisDeduction';
import { useXeroRequestItemsSummary } from './XeroRequestItemsSummary.hooks';
import { messages } from './XeroRequestItemsSummary.messages';
import { XeroRequestItemsSummaryProps } from './XeroRequestItemsSummary.types';

export const XeroRequestItemsSummary = memo<XeroRequestItemsSummaryProps>((props) => {
    const { bem, qa, request, ocrData } = props;

    const { cisSettings } = useSelector(getXeroContext);
    const { summary, totalTax, lineItems, templateName, showSubtotalWarning, showTaxTotalWarning, showTotalWarning } =
        useXeroRequestItemsSummary(props);

    const { details, currency, companyId } = request;
    const { lineAmountType } = details;

    return (
        <SummarySection>
            <Flex direction='column' spacing='4'>
                {summary.amountMismatches.map((errorText) => (
                    <Text
                        font='label'
                        fontSize='small'
                        color='midnight70'
                        key={errorText}
                        data-qa={bem('footer-amount-mismatch-item')}
                    >
                        {errorText}
                    </Text>
                ))}
            </Flex>

            <Flex direction='column' spacing='4' width={420}>
                <WarningTooltip
                    open={showSubtotalWarning}
                    activator={
                        <SummaryLineValues
                            label={messages.footerSubtotalText}
                            value1={intl.formatCurrency(summary.subtotalAmount, currency)}
                            warning={showSubtotalWarning}
                            qa={qa('subtotal-footer-line')}
                        />
                    }
                >
                    <DifferenceWarning
                        value1={summary.subtotalAmount}
                        value2={ocrData?.document?.subTotal}
                        label1={messages.differentSubtotalWarningLabel1({ templateName })}
                        label2={messages.differentSubtotalWarningLabel2}
                        currency={currency}
                        description={messages.differentSubtotalWarningDescription({ templateName })}
                    />
                </WarningTooltip>

                {lineAmountType !== domain.LineAmountType.NoTax &&
                    summary.taxInfo.map((taxItem, index) => (
                        <Fragment key={taxItem.taxRateString}>
                            {index === summary.taxInfo.length - 1 ? (
                                <WarningTooltip
                                    open={showTaxTotalWarning}
                                    activator={
                                        <SummaryLineValues
                                            label={`${taxItem.taxRateString}:`}
                                            value1={intl.formatCurrency(taxItem.taxAmount, currency)}
                                            warning={showTaxTotalWarning}
                                            qa={qa('tax-component-footer-line')}
                                        />
                                    }
                                >
                                    <DifferenceWarning
                                        value1={totalTax}
                                        value2={ocrData?.document?.totalTax}
                                        label1={messages.differentTaxTotalWarningLabel1({ templateName })}
                                        label2={messages.differentTaxTotalWarningLabel2}
                                        currency={currency}
                                        description={messages.differentTaxTotalWarningDescription({ templateName })}
                                    />
                                </WarningTooltip>
                            ) : (
                                <SummaryLineValues
                                    label={`${taxItem.taxRateString}:`}
                                    value1={intl.formatCurrency(taxItem.taxAmount, currency)}
                                    qa={qa('tax-component-footer-line')}
                                />
                            )}
                        </Fragment>
                    ))}

                {Boolean(summary.taxAdjustmentsAmount) && (
                    <SummaryLineValues
                        label={messages.taxAdjustmentsTextLabel}
                        value1={intl.formatCurrency(summary.taxAdjustmentsAmount, currency)}
                        qa={qa('tax-adjustment-footer-line')}
                    />
                )}

                <Divider color='midnight80' spacing='8 0' />

                <WarningTooltip
                    open={showTotalWarning}
                    activator={
                        <SummaryLineTotal
                            label={messages.footerTotalText}
                            value1={intl.formatCurrency(summary.totalAmount, currency)}
                            warning={showTotalWarning}
                        />
                    }
                >
                    <DifferenceWarning
                        value1={summary.totalAmount}
                        value2={ocrData?.document?.total}
                        label1={messages.differentTotalWarningLabel1({ templateName })}
                        label2={messages.differentTotalWarningLabel2}
                        currency={currency}
                        description={messages.differentTotalWarningDescription({ templateName })}
                    />
                </WarningTooltip>

                {(details.integrationCode === domain.IntegrationCode.XeroBill ||
                    details.integrationCode === domain.IntegrationCode.XeroInvoice) && (
                    <XeroCisDeduction
                        companyId={companyId}
                        details={details}
                        organizationCisSettings={cisSettings}
                        lineItems={lineItems}
                        currency={currency}
                    />
                )}
            </Flex>
        </SummarySection>
    );
});

XeroRequestItemsSummary.displayName = 'XeroRequestItemsSummary';
